import React, { useEffect, useState } from 'react';
import { ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline';
import {
  getToken,
  verifyToken,
  clearToken,
  clearUserName,
  clearRememberMe,
  getLoginRoute,
} from './utils/TokenUtils';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { SignInShell } from './shell/SignInShell';
import {
  buildAuthorizationUrl,
  isWorkosConfigured,
  WORKOS_STATE_KEY,
} from './utils/workosConfig';

/**
 * Sign in — a door, not a form.
 *
 * The email/password form, the Google button and the "remember me"
 * checkbox all lived here until WorkOS took over authentication. They are
 * gone rather than hidden: AuthKit's hosted page now owns credentials,
 * password reset, social sign-in and MFA, and keeping a second way in
 * would mean keeping the surface we are paying to stop owning.
 *
 * **The password endpoint still exists**, but the backend refuses it for
 * every `user_type` except SuperAdmin — a break-glass for operating
 * during a WorkOS outage. It is deliberately not reachable from this
 * page; someone using it is doing so knowingly, with a tool.
 *
 * The round trip: this page sends the browser to AuthKit, AuthKit returns
 * it to `/callback` with a single-use code, and `WorkosCallbackPage` posts
 * that code to our backend, which exchanges it and mints *our* tokens.
 */
const LoginPage: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [redirecting, setRedirecting] = useState<boolean>(false);

  // Someone arriving with a live session should not have to sign in
  // again. Unchanged in intent from the form version — only what happens
  // when the check *fails* is different, because there is no form to fall
  // back to and no remembered username to prefill.
  useEffect(() => {
    const verifyAuth = async () => {
      const token = getToken();
      if (!token) {
        clearToken();
        clearRememberMe();
        clearUserName();
        return;
      }

      try {
        const verified = await verifyToken(token);
        if (verified) {
          const route = getLoginRoute();
          if (!route) {
            throw new Error('Failed to get route.');
          }
          window.location.href = route;
        }
      } catch (err) {
        console.error('Token verification failed:', err);
      }
    };

    verifyAuth();
  }, []);

  // Leaves the app entirely: AuthKit is a hosted page, so this is a
  // full-page navigation rather than a popup or an embedded form.
  const handleSignIn = () => {
    if (!isWorkosConfigured()) {
      // Better than a button that silently does nothing. An unset client
      // ID is a deployment mistake, and the person hitting it cannot fix
      // it themselves.
      setError(
        'Sign-in is not configured for this environment. Please contact support.'
      );
      return;
    }

    const { url, state } = buildAuthorizationUrl();
    // Session storage, not local: the value is meaningless after this
    // round trip and should not outlive the tab. `WorkosCallbackPage`
    // compares it against the `state` AuthKit sends back, which is what
    // makes the round trip proof against login CSRF.
    sessionStorage.setItem(WORKOS_STATE_KEY, state);
    setRedirecting(true);
    window.location.href = url;
  };

  // The portal's own frame (#667): the shell every page sits in, and in
  // its panel the quiet centred block a list shows when it is empty —
  // `ErrorPage`'s idiom, the small accent button included.
  return (
    <SignInShell>
      <div className="mx-auto flex max-w-sm flex-col items-center px-4 py-24 text-center">
        <ArrowRightEndOnRectangleIcon
          aria-hidden="true"
          className="size-8 text-fg-faint"
        />
        <h1 className="mt-4 text-[14px] font-medium text-fg">
          Sign in to your account
        </h1>
        <p className="mt-1 text-[13px]/5 text-fg-muted">
          You&rsquo;ll be taken to our secure sign-in page.
        </p>

        <ErrorAlert className="mt-5 w-full text-left">{error}</ErrorAlert>

        <button
          type="button"
          onClick={handleSignIn}
          disabled={redirecting}
          className="mt-5 inline-flex items-center rounded-md bg-accent px-2.5 py-1 text-[12px] font-medium text-white hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 disabled:opacity-60"
        >
          {redirecting ? 'Redirecting…' : 'Sign in'}
        </button>
      </div>
    </SignInShell>
  );
};

export default LoginPage;
